import type { VisibilityCheck, VisibilityResult } from '@/lib/visibility/analyse';
import VisibilityPreview from '@/components/visibility/VisibilityPreview';

interface Props {
  result: VisibilityResult;
}

/**
 * Renders the free report for a visitor.
 *
 * Two deliberate choices:
 *
 * 1. Server-rendered, not markdown in a `<pre>`. A visitor reads this on a phone, often
 *    on a bad connection, often while worried. Dense markdown is hostile and would suppress
 *    the exact behaviour we need — sending the brief to someone who can act on it.
 *
 * 2. The closing section states plainly that the reader does not need us to act on this,
 *    and presents doing-it-themselves, their web designer and an agency as genuinely equal
 *    options. That is `docs/DECISION-standalone-visibility-model.md` encoded in the product
 *    rather than only in a document. Do not add a sign-up wall to see the full report.
 */
export default function VisibilityReport({ result }: Props) {
  const { score, checks, host, finalUrl } = result;
  const failed = checks.filter((c) => !c.pass).sort((a, b) => b.weight - a.weight);
  const passed = checks.filter((c) => c.pass);

  const tone =
    score >= 85 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : score >= 55 ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-red-700 bg-red-50 border-red-200';

  const verdict =
    score >= 85
      ? 'Your website is in good shape for search and AI. Focus on the few items below, and on getting mentioned elsewhere.'
      : score >= 55
        ? 'Your website is partly set up. Search engines and AI can find you, but they are missing the details customers actually search for.'
        : 'Your website is currently invisible to AI assistants and weak in local search. The good news: most of this is a one-time fix, not a monthly retainer.';

  return (
    <div className="space-y-8">
      {/* Score */}
      <section className={`rounded-2xl border p-6 ${tone}`}>
        <p className="text-xs font-semibold uppercase tracking-widest opacity-80">Visibility score</p>
        <p className="mt-1 text-5xl font-black tracking-tight">
          {score}
          <span className="text-2xl font-bold opacity-60">/100</span>
        </p>
        <p className="mt-1 text-sm font-medium break-all">{host}</p>
        <p className="mt-3 text-sm leading-relaxed">{verdict}</p>
        <p className="mt-2 text-sm opacity-80">
          You pass {checks.length - failed.length} of {checks.length} checks.
        </p>
      </section>

      {/* Before / after — the reason anyone cares about the gap list */}
      {result.preview ? <VisibilityPreview preview={result.preview} /> : null}

      {/* Gaps */}
      {failed.length > 0 ? (
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Do these first</h2>
          <p className="mt-1 text-sm text-slate-600">
            Ordered by how much each one is costing you.
          </p>
          <ol className="mt-4 space-y-3">
            {failed.map((check, index) => (
              <li key={check.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-semibold text-slate-900">
                  <span className="mr-2 text-slate-400">{index + 1}.</span>
                  {check.label}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{check.why}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-800">
                  <strong>What to do:</strong> {check.fix}
                </p>
              </li>
            ))}
          </ol>
        </section>
      ) : (
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Nothing urgent</h2>
          <p className="mt-1 text-sm text-slate-600">
            Everything we check is in place. Focus on getting mentioned by other credible places.
          </p>
        </section>
      )}

      {/* Designer brief */}
      <section className="rounded-2xl border-2 border-[#001489]/20 bg-[#001489]/[0.03] p-5">
        <h2 className="text-lg font-bold text-slate-900">
          Forward this to whoever built your website
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          If you did not build your website yourself, copy everything in this box into an email
          or WhatsApp message to your web designer. You do not need to understand any of it,
          and you should not need to pay anyone extra if they already built your site for you.
        </p>
        <pre className="mt-4 max-h-96 overflow-auto rounded-xl bg-slate-950 p-4 text-xs leading-relaxed text-emerald-300">
          <code>{`Website: ${finalUrl}

Please add this structured data block to the <head> of the homepage:

${result.suggestedSchema}

Also please confirm that /robots.txt allows AI crawlers
(GPTBot, ClaudeBot, PerplexityBot, CCBot), and that the page has
a title, a meta description and a preview image.`}</code>
        </pre>
      </section>

      {/* The honest bit */}
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <h2 className="text-lg font-bold text-slate-900">
          The part a website cannot fix on its own
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          Everything above makes your website <em>understandable</em>. It does not make it{' '}
          <em>trusted</em>, and those are different things.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          Search engines and AI assistants mostly decide what to recommend based on whether other
          credible places already mention you — local directories, industry bodies, news
          coverage, and other businesses linking to you. You cannot fix that from inside your
          own website, and we would rather say so now than let you find out in six months.
        </p>

        <table className="mt-4 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-300 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3">Option</th>
              <th className="py-2">What it actually costs you</th>
            </tr>
          </thead>
          <tbody className="align-top">
            <tr className="border-b border-slate-200">
              <td className="py-2.5 pr-3 font-medium text-slate-900">Do it yourself</td>
              <td className="py-2.5 text-slate-700">
                Your own time. You know your market better than anyone.
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2.5 pr-3 font-medium text-slate-900">Ask your web designer</td>
              <td className="py-2.5 text-slate-700">
                They already have access. Do be aware they may bill you for this.
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2.5 pr-3 font-medium text-slate-900">Hire an SEO agency</td>
              <td className="py-2.5 text-slate-700">
                R8,000&ndash;R25,000/month, for work you could largely do yourself.
              </td>
            </tr>
            <tr>
              <td className="py-2.5 pr-3 font-medium text-slate-900">Zamzi Connect</td>
              <td className="py-2.5 text-slate-700">
                R249/month, or R1,499/month if we work the outreach list with you. Cancel any
                time &mdash; your website stays yours either way.
              </td>
            </tr>
          </tbody>
        </table>

        <p className="mt-4 rounded-lg border border-[#001489]/20 bg-white p-3 text-sm leading-relaxed text-slate-800">
          <strong>You do not need us, a subscription, or anything else from this report to act
          on it.</strong> Everything above is yours to keep, and the brief is complete enough
          to hand to any developer anywhere. If fixing it yourself makes more sense, do that.
        </p>
      </section>

      {/* Passes */}
      {passed.length > 0 ? (
        <details className="rounded-2xl border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700">
            What already passes ({passed.length})
          </summary>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
            {passed.map((check: VisibilityCheck) => (
              <li key={check.id} className="flex gap-2">
                <span aria-hidden="true" className="text-emerald-600">
                  ✓
                </span>
                {check.label}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}